import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";

import openSocket from "../../services/socket-io";
import { AuthContext } from "../../context/Auth/AuthContext";

const DURATION = 5; // segundos

const useStyles = makeStyles((theme) => ({
	"@keyframes taPop": {
		"0%": { opacity: 0, transform: "scale(.8)" },
		"60%": { transform: "scale(1.05)" },
		"100%": { opacity: 1, transform: "scale(1)" },
	},
	"@keyframes taFadeIn": {
		from: { opacity: 0 },
		to: { opacity: 1 },
	},
	"@keyframes taPulse": {
		"0%": { boxShadow: "0 0 0 0 rgba(16,185,129,.6)" },
		"70%": { boxShadow: "0 0 0 26px rgba(16,185,129,0)" },
		"100%": { boxShadow: "0 0 0 0 rgba(16,185,129,0)" },
	},
	"@keyframes taBorder": {
		"0%": { backgroundPosition: "0 0, 0% 50%" },
		"50%": { backgroundPosition: "0 0, 100% 50%" },
		"100%": { backgroundPosition: "0 0, 0% 50%" },
	},
	"@keyframes taTimer": {
		from: { width: "100%" },
		to: { width: "0%" },
	},
	overlay: {
		position: "fixed",
		inset: 0,
		zIndex: 4000,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		background: "rgba(2, 6, 23, 0.72)",
		backdropFilter: "blur(8px) saturate(.85)",
		"-webkit-backdrop-filter": "blur(8px) saturate(.85)",
		animation: "$taFadeIn .3s ease both",
		cursor: "pointer",
	},
	card: {
		width: 560,
		maxWidth: "calc(100vw - 40px)",
		borderRadius: 24,
		border: "3px solid transparent",
		background: `linear-gradient(${
			theme.palette.type === "dark" ? "#0F172A, #0F172A" : "#fff, #fff"
		}) padding-box, linear-gradient(120deg, #10B981, #34D399, #3B82F6, #8B5CF6, #10B981) border-box`,
		backgroundSize: "auto, 300% 300%",
		animation: "$taPop .45s cubic-bezier(.2,.9,.3,1.2) both, $taBorder 4s ease infinite",
		boxShadow: "0 30px 90px rgba(2,6,23,.6)",
		padding: "40px 36px 28px",
		textAlign: "center",
		color: theme.palette.text.primary,
		overflow: "hidden",
	},
	icon: {
		width: 84,
		height: 84,
		margin: "0 auto 20px",
		borderRadius: 26,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
		color: "#fff",
		animation: "$taPulse 1.4s ease-out infinite",
	},
	title: {
		fontWeight: 800,
		fontSize: "1.6rem",
		lineHeight: 1.25,
		letterSpacing: "-0.02em",
		marginBottom: 10,
	},
	highlight: { color: "#10B981" },
	sub: {
		fontSize: "1.02rem",
		color: theme.palette.text.secondary,
		marginBottom: 6,
	},
	hint: {
		fontSize: "0.8rem",
		color: theme.palette.text.secondary,
		opacity: 0.7,
		marginBottom: 22,
	},
	// timer embaixo: barra que encolhe + contagem
	timerWrap: {
		marginTop: 6,
	},
	timerText: {
		fontSize: "0.78rem",
		fontWeight: 700,
		color: theme.palette.text.secondary,
		marginBottom: 8,
		letterSpacing: "0.04em",
	},
	timerTrack: {
		height: 8,
		borderRadius: 4,
		background: "rgba(148,163,184,.25)",
		overflow: "hidden",
	},
	timerFill: {
		height: "100%",
		borderRadius: 4,
		background: "linear-gradient(90deg, #10B981, #34D399)",
		animation: `$taTimer ${DURATION}s linear forwards`,
	},
}));

const TransferAlert = () => {
	const classes = useStyles();
	const history = useHistory();
	const { user } = useContext(AuthContext);
	const [alert, setAlert] = useState(null);
	const [secondsLeft, setSecondsLeft] = useState(DURATION);

	useEffect(() => {
		if (!user?.id) return undefined;
		const socket = openSocket();

		// entra na sala de notificacao de forma CONFIAVEL: o socket.io pode
		// ja ter conectado antes deste listener existir (corrida), entao
		// emitimos na hora se conectado e tambem a cada (re)conexao
		const join = () => socket.emit("joinNotification");
		if (socket.connected) join();
		socket.on("connect", join);
		socket.on("reconnect", join);

		socket.on("transferAlert", (data) => {
			if (Number(data.toUserId) !== Number(user.id)) return;
			setAlert(data);
			setSecondsLeft(DURATION);
		});
		return () => {
			socket.disconnect();
		};
	}, [user?.id]);

	useEffect(() => {
		if (!alert) return undefined;
		const tick = setInterval(
			() => setSecondsLeft((s) => Math.max(0, s - 1)),
			1000
		);
		const close = setTimeout(() => setAlert(null), DURATION * 1000);
		return () => {
			clearInterval(tick);
			clearTimeout(close);
		};
	}, [alert]);

	if (!alert) return null;

	return (
		<div
			className={classes.overlay}
			onClick={() => {
				const id = alert.ticketId;
				setAlert(null);
				history.push(`/tickets/${id}`);
			}}
		>
			<div className={classes.card}>
				<div className={classes.icon}>
					<SwapHorizIcon style={{ fontSize: 44 }} />
				</div>
				<Typography className={classes.title}>
					{alert.selfTransfer ? (
						<>
							<span className={classes.highlight}>Você</span> assumiu o
							atendimento
							{alert.prevUserName ? (
								<> que era de {alert.prevUserName}</>
							) : null}
							!
						</>
					) : (
						<>
							<span className={classes.highlight}>
								{alert.fromUserName}
							</span>{" "}
							transferiu um atendimento para você!
						</>
					)}
				</Typography>
				<Typography className={classes.sub}>
					Conversa com <b>{alert.contactName}</b>
				</Typography>
				<Typography className={classes.hint}>
					Clique em qualquer lugar para abrir a conversa
				</Typography>
				<div className={classes.timerWrap}>
					<Typography className={classes.timerText}>
						Fechando em {secondsLeft}s…
					</Typography>
					<div className={classes.timerTrack}>
						{/* key força a animação a reiniciar a cada novo aviso */}
						<div key={alert.ticketId + "-" + alert.toUserId} className={classes.timerFill} />
					</div>
				</div>
			</div>
		</div>
	);
};

export default TransferAlert;
